import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Eraser,
  Search,
  SearchCheck,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import {
  fetchNotificationDates,
  fetchNotificationDepartments,
  fetchNotificationSubdepartments,
} from "../services/api";
import { FiltrosNotification } from "../types";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface NotificationFiltersProps {
  onApplyFilters: (filters: FiltrosNotification) => void;
}

const NotificationFilters: React.FC<NotificationFiltersProps> = ({
  onApplyFilters,
}) => {
  const [departamentos, setDepartamentos] = useState<string[]>([]);
  const [subdepartamentos, setSubdepartamentos] = useState<string[]>([]);
  const [fechas, setFechas] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Estados para los filtros
  const [selectedDepartamento, setSelectedDepartamento] =
    useState<string>("all");
  const [selectedSubdepartamento, setSelectedSubdepartamento] =
    useState<string>("all");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>("");
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Estados para la búsqueda de departamentos
  const [departamentoSearch, setDepartamentoSearch] = useState<string>("");

  // Filtrar departamentos según la búsqueda
  const filteredDepartamentos = useMemo(() => {
    if (!departamentoSearch.trim()) return departamentos;

    return departamentos.filter((dept) =>
      dept.toLowerCase().includes(departamentoSearch.toLowerCase())
    );
  }, [departamentos, departamentoSearch]);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Cargar departamentos
        const depts = await fetchNotificationDepartments();
        setDepartamentos(depts);

        // Cargar fechas
        const dates = await fetchNotificationDates();
        setFechas(dates);

        // Cargar subdepartamentos (todos)
        const subdepts = await fetchNotificationSubdepartments();
        setSubdepartamentos(subdepts);
      } catch (error) {
        console.error("Error cargando datos para filtros:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Cargar subdepartamentos cuando cambia el departamento seleccionado
  useEffect(() => {
    const loadSubdepartamentos = async () => {
      if (selectedDepartamento === "all") {
        // Si no hay departamento seleccionado, cargar todos los subdepartamentos
        try {
          const subdepts = await fetchNotificationSubdepartments();
          setSubdepartamentos(subdepts);
        } catch (error) {
          console.error("Error cargando subdepartamentos:", error);
        }
        return;
      }

      try {
        const subdepts = await fetchNotificationSubdepartments(
          selectedDepartamento
        );
        setSubdepartamentos(subdepts);
      } catch (error) {
        console.error("Error cargando subdepartamentos:", error);
      }
    };

    loadSubdepartamentos();
  }, [selectedDepartamento]);

  // Manejar cambio de departamento
  const handleDepartamentoChange = (value: string) => {
    setSelectedDepartamento(value);
    setSelectedSubdepartamento("all"); // Resetear subdepartamento al cambiar departamento
  };

  // Manejar selección de fecha
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setCalendarOpen(false); // Cerrar el calendario al seleccionar una fecha
  };

  // Manejar aplicación de filtros
  const handleApplyFilters = () => {
    const filters: FiltrosNotification = {};

    if (selectedDepartamento !== "all")
      filters.departamento = selectedDepartamento;
    if (selectedSubdepartamento !== "all")
      filters.subdepartamento = selectedSubdepartamento;
    if (selectedDate) {
      // Formatear la fecha como YYYY-MM-DD
      filters.fecha = format(selectedDate, "yyyy-MM-dd");
    }
    if (searchText) filters.search_text = searchText;

    onApplyFilters(filters);
  };

  // Manejar reset de filtros
  const handleResetFilters = () => {
    setSelectedDepartamento("all");
    setSelectedSubdepartamento("all");
    setSelectedDate(undefined);
    setSearchText("");

    onApplyFilters({});
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Filtro por departamento */}
        <div className="space-y-2">
          <Label htmlFor="departamento">Departamento</Label>
          <Select
            value={selectedDepartamento}
            onValueChange={handleDepartamentoChange}
          >
            <SelectTrigger id="departamento">
              <SelectValue placeholder="Todos los departamentos" />
            </SelectTrigger>
            <SelectContent
              searchable={true}
              onSearch={(value) => setDepartamentoSearch(value)}
            >
              <SelectItem value="all">Todos los departamentos</SelectItem>
              {filteredDepartamentos.map((dept) => (
                <SelectItem key={dept} value={dept}>
                  {dept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Filtro por subdepartamento */}
        <div className="space-y-2">
          <Label htmlFor="subdepartamento">Subdepartamento</Label>
          <Select
            value={selectedSubdepartamento}
            onValueChange={setSelectedSubdepartamento}
            disabled={loading || subdepartamentos.length === 0}
          >
            <SelectTrigger id="subdepartamento">
              <SelectValue placeholder="Todos los subdepartamentos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los subdepartamentos</SelectItem>
              {subdepartamentos.map((subdept) => (
                <SelectItem key={subdept} value={subdept}>
                  {subdept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Filtro por fecha con datepicker */}
        <div className="space-y-2">
          <Label htmlFor="fecha">Fecha</Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                id="fecha"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? (
                  format(selectedDate, "dd/MM/yyyy")
                ) : (
                  <span>Seleccionar fecha</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Búsqueda por texto */}
        <div className="space-y-2">
          <Label htmlFor="search">Buscar</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="search"
              type="text"
              placeholder="Buscar en notificaciones..."
              className="pl-8"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText("")}
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
              >
                <Eraser className="h-4 w-4" />
                <span className="sr-only">Limpiar búsqueda</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={handleResetFilters}>
          <Eraser className="h-4 w-4 mr-2" />
          Limpiar filtros
        </Button>
        <Button onClick={handleApplyFilters}>
          <SearchCheck className="h-4 w-4 mr-2" />
          Aplicar filtros
        </Button>
      </div>
    </div>
  );
};

export default NotificationFilters;
